import React, { FC, forwardRef, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from '../Typography';
import { BaseFormProps } from './Base';
import { FormFieldInline } from './FormFieldInline';

const StyledRadioWrapper = styled.div`
  ${({ theme: { space, colors, borders } }) => css`
    label {
      position: relative;

      &:before {
        content: '';
        width: 24px;
        height: 24px;
        border: ${borders.default};
        border-radius: 100%;
        background: ${colors.white.default};
        transition: all 0.2s ease;
      }

      &:after {
        content: '';
        width: 12px;
        height: 12px;
        background: ${colors.white.default};
        position: absolute;
        top: 6px;
        left: 6px;
        border-radius: 100%;
        opacity: 0;
        transform: scale(0);
        transition: all 0.2s ease;
      }

      &:hover {
        cursor: pointer;
        &:before {
          border-color: ${colors.black.default};
        }
      }
    }

    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;

      &:checked + label {
        &:before {
          background: ${colors.brand.default};
        }

        &:after {
          opacity: 1;
          -webkit-transform: scale(1);
          transform: scale(1);
        }

        &:hover {
          border-color: ${colors.brand.default};
        }
      }
    }
  `};
`;

const StyledHelpText = styled(Typography)`
  ${({ theme: { space, borders } }) => css`
    padding-left: ${space.lg};
    margin-top: ${space.sm};
  `};
`;

export interface RadioProps extends BaseFormProps {
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  name?: string;
  helpText?: string;
}

export const Radio: FC<RadioProps> = forwardRef(
  (
    {
      label,
      id,
      tooltipText,
      tooltipInfoIcon,
      valid,
      invalid,
      constrained,
      required,
      checked,
      value,
      onChange,
      name,
      helpText,
      ...rest
    },
    ref
  ) => (
    <StyledRadioWrapper>
      <FormFieldInline
        label={label}
        htmlFor={id}
        tooltipText={tooltipText}
        tooltipInfoIcon={tooltipInfoIcon}
        required={required}
        {...rest}
      >
        <input
          type="radio"
          id={id}
          name={name || id}
          checked={checked}
          value={value}
          ref={ref as RefObject<HTMLInputElement>}
          onChange={onChange}
        />
      </FormFieldInline>
      {helpText && (
        <StyledHelpText component="p" variant="bodySmall">
          {helpText}
        </StyledHelpText>
      )}
    </StyledRadioWrapper>
  )
);
